import { Trash2 } from "lucide-react";
import UserStatusBadge from "./UserStatusBadge";

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString();
};

const maxWebsites = (list = []) => {
  if (!list.length) return "—";
  if (list.length === 1) return list[0] || "—";
  return `${list.slice(0, 2).join(", ")}${list.length > 2 ? ` +${list.length - 2}` : ""}`;
};

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];

export default function UsersTable({
  users = [],
  onRoleChange,
  onStatusToggle,
  onViewDetails,
  onDelete,
  processing = {},
}) {
  if (!users.length) {
    return <p className="p-6 text-sm text-gray-500">No users found.</p>;
  }

  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3 font-semibold">Email</th>
            <th className="px-4 py-3 font-semibold">Role</th>
            <th className="px-4 py-3 font-semibold">Status</th>
            <th className="px-4 py-3 font-semibold">Website(s)</th>
            <th className="px-4 py-3 font-semibold">Created</th>
            <th className="px-4 py-3 font-semibold">Last Activity</th>
            <th className="px-4 py-3 font-semibold">Articles</th>
            <th className="px-4 py-3 font-semibold">Published</th>
            <th className="px-4 py-3 font-semibold">Errors</th>
            <th className="px-4 py-3 font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {users.map((user) => {
            const isProcessingRole =
              processing?.userId === user.id && processing?.action === "role";
            const isProcessingStatus =
              processing?.userId === user.id && processing?.action === "status";
            const isProcessingDelete =
              processing?.userId === user.id && processing?.action === "delete";

            return (
              <tr key={user.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                  <div>{user.displayName || user.email}</div>
                  <div className="text-xs text-slate-400">{user.email}</div>
                </td>
                <td className="px-4 py-3">
                  <select
                    className="w-full rounded border border-slate-200 px-2 py-1 text-sm text-slate-700"
                    value={user.role || "user"}
                    disabled={isProcessingRole}
                    onChange={(e) => onRoleChange?.(user.id, e.target.value)}
                  >
                    {ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <UserStatusBadge isActive={user.isActive} />
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      disabled={isProcessingStatus}
                      onClick={() => onStatusToggle?.(user.id, !user.isActive)}
                    >
                      {user.isActive ? "Disable" : "Enable"}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {maxWebsites(user.websites)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {formatDate(user.createdAt)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {user.lastActivityAt
                    ? new Date(user.lastActivityAt).toLocaleString()
                    : "—"}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {user.totalArticles ?? 0}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {user.totalPublished ?? 0}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {user.errorCount ?? 0}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onViewDetails?.(user.id)}
                      className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700"
                    >
                      View
                    </button>
                    <button
                      onClick={() => onDelete?.(user.id)}
                      disabled={isProcessingDelete}
                      className="rounded-full bg-red-50 p-1.5 text-red-600 hover:bg-red-100 disabled:opacity-50"
                      title="Delete user"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
